/**
 * One data table of an entity's rules (`GET /rules/{entity}/tables/{table}`), read only.
 *
 * Shows the rows that apply to the company, with the GoA source files the table was
 * taken from, a search over every cell and paging. The table holds company domains,
 * account numbers and Norican codes only (no person data); it is changed by IT in the
 * data files, never here.
 */

import { Icon, Search } from "@norican/icons";
import {
  DataGrid,
  type DataGridColumnDef,
  Dialog,
  Input,
  Pagination,
  Skeleton,
  Typography,
} from "@norican/react";
import { useMemo, useState } from "react";

import { type ApiError, useEntityRuleTable } from "@/lib/api";
import { logger } from "@/lib/logger";

const PAGE_SIZE = 50;

interface TableRow {
  id: string;
  cells: string[];
}

/** The message for a table that cannot be read. */
function failureText(error: unknown): string {
  const status = (error as ApiError | null)?.status;
  if (status === 403) {
    return "The rules are for managers and admins.";
  }
  if (status === 404) {
    return "This table does not belong to the rules of this company.";
  }
  return "I2Q cannot read the table now. Try again in a moment.";
}

export function RuleTableDialog({
  entity,
  entityName,
  table,
  onClose,
}: {
  entity: string;
  /** The company as shown, e.g. "United Kingdom". */
  entityName: string;
  /** The table to show; null when the dialog is closed. */
  table: string | null;
  onClose: () => void;
}) {
  const [text, setText] = useState("");
  const [page, setPage] = useState(1);
  const open = table !== null;
  const result = useEntityRuleTable(
    entity,
    table ?? "",
    { q: text, offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE },
    open,
  );
  const data = result.data;
  const columnNames = useMemo(() => data?.columns ?? [], [data?.columns]);
  const columns = useMemo<DataGridColumnDef<TableRow>[]>(
    () =>
      columnNames.map((name, index) => ({
        id: `c${index}`,
        header: name,
        cell: (row: TableRow) => (
          <span className="text-fg block text-xs break-words whitespace-normal">
            {row.cells[index] ?? ""}
          </span>
        ),
      })),
    [columnNames],
  );
  const rows = useMemo<TableRow[]>(
    () =>
      (data?.rows ?? []).map((cells, index) => ({
        id: `${data?.offset ?? 0}-${index}`,
        cells,
      })),
    [data?.rows, data?.offset],
  );
  const total = data?.total ?? 0;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setText("");
          setPage(1);
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup className="w-[min(56rem,calc(100vw-2rem))] max-w-none">
          <Dialog.Header>
            <Dialog.Title>
              <Typography as="span" variant="heading-6" className="text-fg">
                {table ?? ""} for {entityName}
              </Typography>
            </Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-3">
            <Dialog.Description>
              <Typography variant="body-sm" tone="muted">
                Read only. The rows of this table that apply to {entityName}. IT changes them on
                request.
              </Typography>
            </Dialog.Description>
            {data && (data.sources ?? []).length > 0 && (
              <ul className="flex flex-col gap-0.5" aria-label="Sources">
                {(data.sources ?? []).map((source) => (
                  <li key={source} className="text-fg-subtle text-xs break-words">
                    Source: {source}
                  </li>
                ))}
              </ul>
            )}
            {data && (data.notes ?? []).length > 0 && (
              <ul className="flex flex-col gap-0.5" aria-label="Notes">
                {(data.notes ?? []).map((note) => (
                  <li key={note} className="text-fg-muted text-xs break-words">
                    {note}
                  </li>
                ))}
              </ul>
            )}
            <Input
              type="search"
              value={text}
              maxLength={80}
              onChange={(event) => {
                logger.debug("[rules] table filter", { table, q: event.target.value });
                setText(event.target.value);
                setPage(1);
              }}
              placeholder="Search every column"
              aria-label="Search the table"
              startSlot={<Icon icon={Search} size="sm" className="text-fg-subtle" />}
            />
            {result.isLoading ? (
              <Skeleton variant="rect" height={160} />
            ) : result.isError ? (
              <Typography variant="body-sm" tone="muted" role="alert">
                {failureText(result.error)}
              </Typography>
            ) : rows.length === 0 ? (
              <Typography variant="body-sm" tone="muted">
                {text.trim() ? "No row matches the search." : "This table has no row for it."}
              </Typography>
            ) : (
              <div className="max-h-[60vh] overflow-auto">
                <DataGrid<TableRow>
                  columns={columns}
                  data={rows}
                  getRowId={(row) => row.id}
                  density="compact"
                  aria-label={`Rows of ${table ?? "the table"}`}
                />
              </div>
            )}
            {total > PAGE_SIZE && (
              <Pagination
                page={page}
                total={total}
                pageSize={PAGE_SIZE}
                label="Table pages"
                onPageChange={(next) => {
                  logger.debug("[rules] table page", { table, page: next });
                  setPage(next);
                }}
              />
            )}
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
